import { z } from "zod";
import {
  MAX_BULK_RECORDS,
  personRecordKeySchema,
  sendInputSchema,
  signalKeysSchema,
} from "../http";
import { workflowKeySchema } from "./catalog";
import { runTaskSchema, taskDefinitionSchema, taskKeySchema } from "./tasks";
// A trigger is a workflow ID with a time attached. The client fixes the time
// when the button is first pressed so a retried request after an uncertain
// HTTP result targets the same workflow ID instead of starting a second run.
export const launchWorkflowInput = z.object({
  /** A collection source ID, or the connection ID for a delivery workflow. */
  sourceId: z.string().min(1),
  workflowKey: workflowKeySchema,
  requestedAt: z.iso.datetime().optional(),
  /** Delivery workflows only: the people to send and the signals they came from. */
  keys: z.array(personRecordKeySchema).min(1).max(MAX_BULK_RECORDS).optional(),
  signalKeys: signalKeysSchema.optional(),
});
export type LaunchWorkflowInput = z.infer<typeof launchWorkflowInput>;
export const workflowStartedSchema = z.object({ workflowId: z.string() });
export const workflowCatalogSchema = z.array(
  z.object({
    key: workflowKeySchema,
    name: z.string(),
    description: z.string(),
    schedule: z.string(),
    tasks: z.array(taskDefinitionSchema),
  }),
);
export const DEFAULT_SCHEDULE_CRON = "0 * * * *";
export const scheduleInput = z.object({
  sourceId: z.uuid(),
  workflowKey: workflowKeySchema,
  enabled: z.boolean(),
  cron: z.string().trim().min(1).optional(),
});
export const scheduleParams = scheduleInput.pick({
  sourceId: true,
  workflowKey: true,
});
export const workflowScheduleSchema = z.object({
  id: z.string(),
  sourceId: z.string(),
  sourceName: z.string(),
  workflowKey: workflowKeySchema,
  paused: z.boolean(),
  cron: z.string(),
  nextRun: z.string().nullable(),
});
/** Execution statuses as Temporal reports them, lower-cased for the UI. */
export const runStatusSchema = z.enum([
  "running",
  "completed",
  "failed",
  "cancelled",
  "terminated",
  "timed_out",
  "continued_as_new",
]);
export type RunStatus = z.infer<typeof runStatusSchema>;
export const runListQuery = z.object({
  page: z.coerce.number().int().positive().optional(),
  q: z.string().optional(),
  workflow: workflowKeySchema.optional(),
  status: runStatusSchema.optional(),
  trigger: z.enum(["manual", "schedule"]).optional(),
  repository: z.string().min(1).optional(),
  days: z.coerce.number().int().nonnegative().optional(),
});
export type RunListQuery = z.infer<typeof runListQuery>;
export const runSummarySchema = z.object({
  workflowId: z.string(),
  runId: z.string(),
  workflowKey: z.string(),
  sourceId: z.string().nullable(),
  sourceName: z.string(),
  status: runStatusSchema,
  trigger: z.string(),
  startedAt: z.string(),
  closedAt: z.string().nullable(),
  tasks: z.array(runTaskSchema),
});
export type RunSummary = z.infer<typeof runSummarySchema>;
export const runListSchema = z.object({
  rows: z.array(runSummarySchema),
  page: z.number(),
  total: z.number(),
  hasNext: z.boolean(),
});
export const runDetailSchema = runSummarySchema.extend({
  input: z.unknown(),
  output: z.unknown(),
  // The ingestion ledger row, once the workflow's first activity created it.
  id: z.string().nullable(),
  appliedAt: z.string().nullable(),
  fetched: z.number(),
  added: z.number(),
  removed: z.number(),
  baseline: z.number(),
  error: z.string().nullable(),
  pendingActivities: z.array(
    z.object({
      name: z.string(),
      attempt: z.number(),
      error: z.string().nullable(),
    }),
  ),
  /** Link into the Temporal UI when the deployment exposes one. */
  url: z.string().nullable(),
  /** Delivery workflows: the outcome of every record this run sent. */
  deliveries: z
    .object({
      pending: z.number(),
      sent: z.number(),
      failed: z.number(),
      unknown: z.number(),
      cancelled: z.number(),
    })
    .nullable(),
});
export const runRefQuery = z.object({
  /** A Temporal workflow ID, or the UUID of the run's ingestion ledger row. */
  ref: z.string().min(1),
});
// Retry starts a brand-new run; cancel and terminate act on the executions.
export const controlRunsInput = z.object({
  action: z.enum(["cancel", "terminate", "retry"]),
  workflowIds: z.array(z.string().min(1)).min(1).max(100),
});
export const collectionRequestSchema = z.object({
  sourceId: z.uuid(),
  workflowKey: workflowKeySchema,
  trigger: z.enum(["manual", "schedule"]),
  userId: z.uuid().optional(),
  workflowId: z.string().min(1),
});
export const runIdSchema = z.object({ runId: z.uuid() });
export const collectRequestSchema = runIdSchema.extend({
  attempt: z.number().int().positive(),
  taskKey: taskKeySchema,
});
/** Coverage the collection could not complete is reported, not failed. */
export const collectResultSchema = z.object({ partial: z.boolean() });
export const hydrationRequestSchema = z.object({
  integrationId: z.string(),
  identityIds: z.array(z.uuid()),
});
export const hydrateProfileSchema = z.object({
  integrationId: z.string(),
  identityId: z.uuid(),
});
export const commitEmailsResultSchema = z.object({
  found: z.number().int().nonnegative(),
});
export const prepareSendSchema = sendInputSchema.extend({
  signalKeys: signalKeysSchema.optional(),
  userId: z.uuid().optional(),
  /** The delivery workflow this send runs under; stamped on every row. */
  workflowId: z.string().min(1),
});
export const preparedSendSchema = z.object({ deliveryIds: z.array(z.uuid()) });
export const deliveryRequestSchema = z.object({
  deliveryId: z.uuid(),
  /** Temporal's attempt number, recorded on the attempt row. */
  attempt: z.number().int().positive().optional(),
});
export const deliveryStatusSchema = z.enum(["pending", "sent", "failed", "unknown", "cancelled"]);
export type DeliveryStatus = z.infer<typeof deliveryStatusSchema>;
/**
 * What one attempt produced. Temporal decides whether to try again from
 * `retryable` and `retryAfter`; the row already records the outcome.
 */
export const deliveryOutcomeSchema = z.object({
  status: deliveryStatusSchema,
  error: z.string().nullable(),
  retryable: z.boolean(),
  retryAfter: z.number().nullable(),
});
